<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [closable]="false"
  [closeOnEscape]="true"
  [dismissableMask]="true"
  [blockScroll]="true"
  [style]="{ width: '98vw', height: '94vh' }"
  [contentStyle]="{ padding: '0', overflow: 'hidden' }"
  [draggable]="false"
  [resizable]="false"
  styleClass="ic-lightbox"
  (onHide)="onDialogHide()"
>
  <ng-template pTemplate="header"><span class="ic-sr-only"></span></ng-template>

  <div class="ic-canvas" *ngIf="canCompare">

    <!-- ══ TOOLBAR ══ -->
    <div class="ic-toolbar">
      <button type="button" class="ic-toolbar__close" (click)="close()" title="Close">
        <i class="pi pi-times"></i>
      </button>
      <div class="ic-toolbar__brand">
        <span class="ic-toolbar__label">Photo Progress</span>
        <span class="ic-toolbar__subtitle" *ngIf="viewModel">{{ viewModel.title }}</span>
      </div>

      <div class="ic-toolbar__spacer"></div>

      <div class="ic-toolbar__group" *ngIf="viewModel && (viewModel.pairs?.length || 0) > 1">
        <button type="button" class="ic-pill" (click)="prevPair()" [disabled]="activePairIndex <= 0">
          <i class="pi pi-chevron-left"></i>
        </button>
        <span class="ic-toolbar__counter">{{ activePairIndex + 1 }}/{{ viewModel.pairs.length }}</span>
        <button type="button" class="ic-pill" (click)="nextPair()"
          [disabled]="activePairIndex >= viewModel.pairs.length - 1">
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>

      <span class="ic-toolbar__hint" *ngIf="viewModel && !isZoomed">Scroll to zoom</span>
      <span class="ic-toolbar__zoom-val" *ngIf="isZoomed">{{ zoomPercent }}%</span>
      <button type="button" class="ic-pill" *ngIf="isZoomed" (click)="resetZoom()">
        <i class="pi pi-refresh"></i> Fit
      </button>

      <button type="button" class="ic-pill ic-pill--export" (click)="exportPdf()"
        [disabled]="exporting || !viewModel" title="Export PDF">
        <i class="pi" [ngClass]="exporting ? 'pi-spin pi-spinner' : 'pi-file-pdf'"></i>
        <span>{{ exporting ? 'Generating…' : 'Export PDF' }}</span>
      </button>
    </div>

    <!-- ══ MAIN ══ -->
    <div class="ic-main">

      <!-- State: Pre-analysis -->
      <div class="ic-preselect" *ngIf="!viewModel && !loading">
        <div class="ic-preselect__inner">
          <div class="ic-preselect__cols">
            <div class="ic-preselect__col">
              <span class="ic-ptag ic-ptag--before">Before · {{ beforePhotos.length }}</span>
              <div class="ic-preselect__imgs">
                <img *ngFor="let f of beforePhotos; trackBy: trackByPhotoId"
                  [src]="f.url" [alt]="f.name || 'Before'" loading="lazy" />
              </div>
            </div>
            <div class="ic-preselect__col">
              <span class="ic-ptag ic-ptag--after">After · {{ afterPhotos.length }}</span>
              <div class="ic-preselect__imgs">
                <img *ngFor="let f of afterPhotos; trackBy: trackByPhotoId"
                  [src]="f.url" [alt]="f.name || 'After'" loading="lazy" />
              </div>
            </div>
          </div>
          <div class="ic-preselect__cta">
            <div class="ic-preselect__msg ic-preselect__msg--error" *ngIf="error">
              <i class="pi pi-exclamation-circle"></i>
              <span>{{ error }}</span>
            </div>
            <div class="ic-preselect__msg" *ngIf="!error">
              <i class="pi pi-sparkles"></i>
              <span>Ready — generate a clinical comparison from these photos.</span>
            </div>
            <button type="button" class="ic-cta" *ngIf="aiEnabled" (click)="runAnalysis()"
              [disabled]="!canCompare">
              <i class="pi pi-sparkles"></i> Generate interpretation
            </button>
          </div>
        </div>
      </div>

      <!-- State: Loading -->
      <div class="ic-loading" *ngIf="loading">
        <div class="ic-loading__inner">
          <div class="ic-loader"></div>
          <strong>Analyzing {{ beforePhotos.length + afterPhotos.length }} photos</strong>
          <span>{{ elapsedSeconds }}s elapsed</span>
          <button type="button" class="ic-pill ic-pill--cancel" (click)="cancelAnalysis()">Cancel</button>
        </div>
      </div>

      <!-- State: Comparison + Clinical panel -->
      <ng-container *ngIf="viewModel as vm">
        <div class="ic-workspace" [class.ic-workspace--panel-open]="reportPanelOpen">

          <div class="ic-stage">
            <div class="ic-compare" *ngIf="activePair as pair">
              <div class="ic-photo"
                [class.ic-photo--zoomed]="isZoomed"
                [class.ic-photo--dragging]="isDragging"
                (wheel)="onImageWheel($event)"
                (mousedown)="onPanStart($event)">
                <img *ngIf="pair.beforePhoto?.url"
                  [src]="pair.beforePhoto?.url"
                  [style.transform]="imageTransform"
                  alt="Before" draggable="false" />
                <div class="ic-photo__empty" *ngIf="!pair.beforePhoto?.url">Photo unavailable</div>
                <div class="ic-photo__badge ic-photo__badge--before">
                  <strong>Before</strong>
                  <time>{{ pair.beforeDate }}</time>
                </div>
              </div>

              <div class="ic-split-line"></div>

              <div class="ic-photo"
                [class.ic-photo--zoomed]="isZoomed"
                [class.ic-photo--dragging]="isDragging"
                (wheel)="onImageWheel($event)"
                (mousedown)="onPanStart($event)">
                <img *ngIf="pair.afterPhoto?.url"
                  [src]="pair.afterPhoto?.url"
                  [style.transform]="imageTransform"
                  alt="After" draggable="false" />
                <div class="ic-photo__empty" *ngIf="!pair.afterPhoto?.url">Photo unavailable</div>
                <div class="ic-photo__badge ic-photo__badge--after">
                  <strong>After</strong>
                  <time>{{ pair.afterDate }}</time>
                </div>
              </div>
            </div>

            <div class="ic-filmstrip" *ngIf="vm.pairs.length > 1">
              <button type="button"
                *ngFor="let p of vm.pairs; trackBy: trackByPairIndex"
                [class.active]="p.index === activePairIndex"
                (click)="selectPair(p.index)">
                <img *ngIf="p.afterPhoto?.url || p.beforePhoto?.url"
                  [src]="p.afterPhoto?.url || p.beforePhoto?.url"
                  [alt]="'Pair ' + (p.index + 1)" />
                <span>{{ p.index + 1 }}</span>
              </button>
            </div>
          </div>

          <!-- ══ CLINICAL DASHBOARD (always visible when open) ══ -->
          <aside class="ic-report" *ngIf="reportPanelOpen">
            <div class="ic-report__scroll">

              <!-- Outcome hero -->
              <section class="ic-outcome" [attr.data-tone]="vm.levelTone">
                <div class="ic-outcome__icon">
                  <i class="pi" [ngClass]="vm.levelIcon"></i>
                </div>
                <div class="ic-outcome__body">
                  <span class="ic-outcome__eyebrow">Overall outcome</span>
                  <h3 class="ic-outcome__title">{{ vm.levelLabel || 'Assessment' }}</h3>
                  <p class="ic-outcome__meta" *ngIf="vm.site || activePair">
                    <span *ngIf="vm.site">{{ vm.site }}</span>
                    <span *ngIf="vm.site && activePair">·</span>
                    <span *ngIf="activePair">{{ activePair.beforeDate }} → {{ activePair.afterDate }}</span>
                  </p>
                </div>
                <div class="ic-outcome__legend" *ngIf="vm.metrics.length">
                  <span class="ic-dot ic-dot--positive" *ngIf="vm.metricSummary.positive">
                    {{ vm.metricSummary.positive }}↑
                  </span>
                  <span class="ic-dot ic-dot--caution" *ngIf="vm.metricSummary.caution">
                    {{ vm.metricSummary.caution }}~
                  </span>
                  <span class="ic-dot ic-dot--warning" *ngIf="vm.metricSummary.warning">
                    {{ vm.metricSummary.warning }}!
                  </span>
                  <span class="ic-dot ic-dot--negative" *ngIf="vm.metricSummary.negative">
                    {{ vm.metricSummary.negative }}↓
                  </span>
                  <span class="ic-dot ic-dot--neutral" *ngIf="vm.metricSummary.neutral">
                    {{ vm.metricSummary.neutral }}=
                  </span>
                </div>
              </section>

              <!-- Metric scorecards -->
              <section class="ic-rs" *ngIf="vm.metrics.length">
                <div class="ic-rs__head">
                  <h4>Change at a glance</h4>
                  <div class="ic-legend">
                    <span><i class="ic-swatch ic-swatch--positive"></i> Improving</span>
                    <span><i class="ic-swatch ic-swatch--caution"></i> Mixed</span>
                    <span><i class="ic-swatch ic-swatch--warning"></i> Concerning</span>
                    <span><i class="ic-swatch ic-swatch--negative"></i> Worsening</span>
                  </div>
                </div>
                <div class="ic-mgrid">
                  <article class="ic-mcard" *ngFor="let m of vm.metrics" [attr.data-tone]="m.tone">
                    <header class="ic-mcard__top">
                      <span class="ic-mcard__name">
                        <i class="pi" [ngClass]="m.icon"></i>
                        {{ m.label }}
                      </span>
                      <span class="ic-trend" [attr.data-tone]="m.tone">
                        <i class="pi" [ngClass]="toneIcon(m.tone)"></i>
                        {{ toneWord(m.tone) }}
                      </span>
                    </header>
                    <div class="ic-mcard__change" *ngIf="m.beforeText && m.afterText; else plainMetric">
                      <div class="ic-mcard__side">
                        <em>Before</em>
                        <strong [title]="m.beforeText">{{ shortMetricText(m.beforeText, 48) }}</strong>
                      </div>
                      <i class="pi pi-arrow-right ic-mcard__arrow" aria-hidden="true"></i>
                      <div class="ic-mcard__side">
                        <em>After</em>
                        <strong [title]="m.afterText">{{ shortMetricText(m.afterText, 48) }}</strong>
                      </div>
                    </div>
                    <ng-template #plainMetric>
                      <p class="ic-mcard__plain" [title]="m.text">{{ shortMetricText(m.text, 90) }}</p>
                    </ng-template>
                  </article>
                </div>
              </section>

              <!-- Findings -->
              <section class="ic-rs" *ngIf="activePair?.beforeFindings || activePair?.afterFindings">
                <h4>Key findings</h4>
                <div class="ic-findings">
                  <div class="ic-finding ic-finding--before" *ngIf="activePair?.beforeFindings">
                    <span class="ic-finding__tag">Before</span>
                    <p>{{ activePair?.beforeFindings }}</p>
                  </div>
                  <div class="ic-finding__bridge" *ngIf="activePair?.beforeFindings && activePair?.afterFindings"
                    aria-hidden="true">
                    <i class="pi pi-arrow-right"></i>
                  </div>
                  <div class="ic-finding ic-finding--after" *ngIf="activePair?.afterFindings">
                    <span class="ic-finding__tag">After</span>
                    <p>{{ activePair?.afterFindings }}</p>
                  </div>
                </div>
              </section>

              <!-- Summary -->
              <section class="ic-rs" *ngIf="vm.intervalComparison">
                <h4>Clinical summary</h4>
                <p class="ic-rtext">{{ vm.intervalComparison }}</p>
              </section>

              <!-- Care & Notes -->
              <section class="ic-rs" *ngIf="vm.recommendedCare || vm.clinicianNotes">
                <h4>Care & notes</h4>
                <div class="ic-rcards">
                  <div class="ic-rcard" *ngIf="vm.recommendedCare">
                    <h5><i class="pi pi-heart"></i> Recommended care</h5>
                    <p>{{ vm.recommendedCare }}</p>
                  </div>
                  <div class="ic-rcard" *ngIf="vm.clinicianNotes">
                    <h5><i class="pi pi-file"></i> Clinician notes</h5>
                    <p>{{ vm.clinicianNotes }}</p>
                  </div>
                </div>
              </section>

              <!-- Caveats -->
              <section class="ic-rs ic-rs--warn" *ngIf="vm.caveats.length">
                <h4>Comparability notes</h4>
                <ul class="ic-cavlist">
                  <li *ngFor="let c of vm.caveats">{{ c }}</li>
                </ul>
              </section>

              <p class="ic-watermark">Confidential — {{ patientExportLabel }}</p>
            </div>
          </aside>
        </div>
      </ng-container>
    </div>

    <!-- ══ BOTTOM BAR ══ -->
    <div class="ic-bar">
      <div class="ic-bar__status" *ngIf="viewModel as vm" [attr.data-tone]="vm.levelTone">
        <i class="pi" [ngClass]="vm.levelIcon"></i>
        <strong>{{ vm.levelLabel }}</strong>
        <span *ngIf="vm.site">· {{ vm.site }}</span>
      </div>
      <div class="ic-bar__spacer"></div>
      <button type="button" class="ic-bar__btn" *ngIf="loading" (click)="cancelAnalysis()">Cancel</button>
      <button type="button" class="ic-bar__btn ic-bar__btn--primary" *ngIf="aiEnabled"
        (click)="runAnalysis()" [disabled]="loading || !canCompare">
        <i class="pi" [ngClass]="loading ? 'pi-spin pi-spinner' : 'pi-sparkles'"></i>
        {{ loading ? 'Analyzing…' : (analysis ? 'Regenerate' : 'Analyze') }}
      </button>
      <button type="button" class="ic-bar__btn ic-bar__btn--accent" *ngIf="viewModel"
        (click)="toggleReportPanel()">
        <i class="pi pi-chart-bar"></i>
        {{ reportPanelOpen ? 'Hide report' : 'Show report' }}
      </button>
    </div>
  </div>
</p-dialog>
